import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../../Config';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';

const QuotationClient = () => {
   // Hide sidebar and navbar elements when this component is mounted, restore on unmount
   useEffect(() => {
      const selectors = [
         '.sidebar',
         '#sidebar',
         '.nav',
         '.navbar',
         '.topbar',
         '.side-nav',
         '.app-sidebar',
         '.app-navbar',
         '.layout-sidebar',
         '.left-sidebar',
         'aside',
      ];
      const prevStyles = new Map();
      const hiddenEls = [];

      selectors.forEach((sel) => {
         document.querySelectorAll(sel).forEach((el) => {
            // store previous inline display style
            prevStyles.set(el, el.style.display || '');
            el.style.display = 'none';
            hiddenEls.push(el);
         });
      });

      // Save and adjust body layout to use full width while on this page
      const prevBodyOverflow = document.body.style.overflow || '';
      const prevBodyMarginLeft = document.body.style.marginLeft || '';
      document.body.style.marginLeft = '0';
      document.body.style.overflow = 'auto';

      return () => {
         // restore previous styles
         hiddenEls.forEach((el) => {
            const prev = prevStyles.get(el);
            el.style.display = prev !== undefined ? prev : '';
         });
         document.body.style.overflow = prevBodyOverflow;
         document.body.style.marginLeft = prevBodyMarginLeft;
      };
   }, []);

   // Fetch quotation and its discounts based on route param id
   const { id } = useParams();
   // const navigate = useNavigate();
   const [quotation, setQuotation] = useState(null);
   const [quotationDiscounts, setQuotationDiscounts] = useState([]);
   const [selectedDiscountIds, setSelectedDiscountIds] = useState([]);
   const [quotationAddons, setQuotationAddons] = useState([]);
   const [selectedAddonIds, setSelectedAddonIds] = useState([]);
   const [loadingData, setLoadingData] = useState(false);
   const [fetchError, setFetchError] = useState(null);
   const [logoUrl, setLogoUrl] = useState(null);
   const [serviceProviderName, setServiceProviderName] = useState(null);

   const [otpInput, setOtpInput] = useState('');
   const [showOtpForm, setShowOtpForm] = useState(true);
   const [infoMessage, setInfoMessage] = useState(null);
   const [requestingOtp, setRequestingOtp] = useState(false);
   const [submittingOtp, setSubmittingOtp] = useState(false);

   // new states for submitting client-selected discounts
   const [submittingClientDiscounts, setSubmittingClientDiscounts] = useState(false);
   const [submitMessage, setSubmitMessage] = useState(null);
   const [submitError, setSubmitError] = useState(null);
   // show inline thank-you success view after submission (persisted)
   const [clientSubmitted, setClientSubmitted] = useState(false);
   const [clientPreviouslySubmitted, setClientPreviouslySubmitted] = useState(false);
   const [pdfPath, setPdfPath] = useState(null);
   const [resError, setResError] = useState(null);

   // helper to toggle selection
   const toggleDiscount = (id) => {
      setSelectedDiscountIds((prev) =>
         prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
   };

   // helper to toggle addon selection
   const toggleAddon = (id) => {
      setSelectedAddonIds((prev) =>
         prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
   };

   // Fetch function which optionally includes otp as query param
   const fetchData = async (otp) => {
      if (!id) return;
      setLoadingData(true);
      setFetchError(null);
      try {
         const res = await axios.get(`${API_URL}quotation/${id}/discounts`, {
            params: otp ? { otp } : {},
            headers: { Authorization: `Bearer ${getAuthToken()}` },
         });
         console.log(res.status, "status")
         const resp = res?.data?.response;
         const qData = resp?.quotation ?? null;
         setQuotation(qData);

         // If server says client accepted, update submitted flag
         if (qData?.clientAccepted) {
            setClientPreviouslySubmitted(true);
         }

         setQuotationDiscounts(resp?.quotationDiscounts ?? []);
         setSelectedDiscountIds([]);

         // Also fetch add-ons for this quotation
         try {
            const addonsRes = await axios.get(`${API_URL}quotation/${id}/addons`, {
               params: otp ? { otp } : {},
               headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            const addonsResp = addonsRes?.data?.response;
            setQuotationAddons(addonsResp?.quotationAddons ?? []);
            setSelectedAddonIds([]);
         } catch (addonsErr) {
            console.error('Failed to fetch quotation services', addonsErr);
            setQuotationAddons([]);
            setLoadingData(false);
         }

         setShowOtpForm(false);
         setInfoMessage(null);
      } catch (err) {
         console.error('Failed to fetch quotation discounts', err);
         const statusCode = err?.response?.data?.statusCode;
         const message = err?.response?.data?.message || err.message || 'Failed to fetch';
         // const message = err?.response?.data?.message || err.message || 'Failed to fetch';
         //    console.log(message);
         //    toast.error(message, { autoClose: 3000 });
         if (statusCode === 401 || statusCode === 403) {
            // show OTP form and information returned from server
            setShowOtpForm(true);
            setInfoMessage(message);
            setResError(message);

         } else {
            setFetchError(err);
            setQuotation(null);
            setQuotationDiscounts([]);
            setQuotationAddons([]);
         }
      } finally {
         setLoadingData(false);
         setRequestingOtp(false);
         setSubmittingOtp(false);
         setOtpInput('');
      }
   };

   // Fetch logo using simple axios
   useEffect(() => {
      if (!id) return;
      const fetchLogo = async () => {
         try {
            const res = await axios.get(`${API_URL}quotation/${id}/logo`);
            if (res?.data?.response?.logo) {
               setLogoUrl(res.data.response.logo);
            }
            if (res?.data?.response?.serviceProviderName) {
               setServiceProviderName(res.data.response.serviceProviderName);
            }
            // Check if already accepted from server status
            if (res?.data?.response?.clientAccepted) {
               // Only set previously submitted if we're not currently submitting a fresh response
               setClientPreviouslySubmitted((prev) => {
                  // If we already set clientSubmitted (immediate success), keep it so they see PDF
                  return true;
               });
               setShowOtpForm(false);
            }
         } catch (err) {
            console.error('Failed to fetch logo', err);
         }
      };
      fetchLogo();
   }, [id]);

   useEffect(() => {
      // whenever id changes, reset to OTP form
      setQuotation(null);
      setQuotationDiscounts([]);
      setSelectedDiscountIds([]);
      setQuotationAddons([]);
      setSelectedAddonIds([]);
      setOtpInput('');
      setShowOtpForm(true);
      setInfoMessage(null);
      setPdfPath(null);

      // check per-quotation submitted flag. If present for this quotation id, show thank-you inline view
      try {
         if (id) {
            const key = `clientDiscountSubmitted:${id}`;
            const saved = localStorage.getItem(key);
            if (saved) {
               // handle stored JSON with timestamp or legacy '1' flag
               try {
                  const parsed = JSON.parse(saved);
                  if (parsed && parsed.ts) {
                     const age = Date.now() - Number(parsed.ts);
                     const RECENT_MS = 1 * 60 * 1000; // 1 minute
                     if (age < RECENT_MS) {
                        setClientSubmitted(true); // recently submitted in same session/window
                     } else {
                        setClientPreviouslySubmitted(true); // previous submission on revisit
                     }
                     // Restore PDF path from localStorage
                     if (parsed.pdfPath) setPdfPath(parsed.pdfPath);
                  } else {
                     // legacy value (e.g., '1') -> treat as previous submission
                     setClientPreviouslySubmitted(true);
                  }
               } catch (e) {
                  // not JSON - treat as previous submission
                  setClientPreviouslySubmitted(true);
               }
               setShowOtpForm(false);
            }
         }
      } catch (e) {
         /* ignore */
      }
   }, [id]);

   const handleRequestOtp = async () => {
      setRequestingOtp(true);
      setInfoMessage(null);
      toast.info('Sending OTP...', { autoClose: 2000 });
      try {
         const res = await axios.post(
            `${API_URL}quotation/${id}/resend-otp`,
            {},
            {
               headers: { Authorization: `Bearer ${getAuthToken()}` },
            },
         );
         const resData = res?.data;
         if (resData?.statusCode === 200) {
            // OTP resent successfully
            toast.success('OTP has been resent to your email!', { autoClose: 3000 });
            setOtpInput(''); // Clear the input field
            setShowOtpForm(true); // Keep OTP form visible
            setInfoMessage(null);
         } else {
            const errorMsg = resData?.message || 'Failed to resend OTP';
            toast.error(errorMsg, { autoClose: 3000 });
            setInfoMessage(errorMsg);
         }
      } catch (err) {
         console.error('Failed to resend OTP', err);
         const message = err?.response?.data?.message || err.message || 'Failed to resend OTP';
         toast.error(message, { autoClose: 3000 });
         setInfoMessage(message);
      } finally {
         setRequestingOtp(false);
      }
   };

   const handleSubmitOtp = async (e) => {
      e.preventDefault();
      if (!otpInput) return setInfoMessage('Please enter OTP');
      setSubmittingOtp(true);
      await fetchData(otpInput.trim());
   };

   // submit selected discounts and add-ons as client selections (single unified API)
   const handleSubmitSelections = async () => {
      if (!id) return;
      setSubmitMessage(null);
      setSubmitError(null);

      const hasDiscounts = selectedDiscountIds && selectedDiscountIds.length > 0;
      const hasAddons = selectedAddonIds && selectedAddonIds.length > 0;

      if (!hasDiscounts && !hasAddons) {
         setSubmitError('Please select at least one discount or service to submit.');
         return;
      }

      setSubmittingClientDiscounts(true);
      try {
         // Build unified payload
         const discountPayload = hasDiscounts
            ? quotationDiscounts
               .filter((d) => selectedDiscountIds.includes(d.id))
               .map((d) => ({
                  quotationDiscountId: d.id,
                  type: d.type ?? d.discount?.type,
                  value: Number(d.value ?? d.discount?.value ?? 0),
               }))
            : [];

         const addonPayload = hasAddons
            ? quotationAddons
               .filter((a) => selectedAddonIds.includes(a.id))
               .map((a) => ({
                  quotationAddonId: a.id,
                  type: a.type ?? a.addonService?.type,
                  value: Number(a.value ?? a.addonService?.value ?? 0),
               }))
            : [];

         const res = await axios.post(
            `${API_URL}quotation/${id}/client-selections`,
            { discounts: discountPayload, addons: addonPayload },
            {
               headers: { Authorization: `Bearer ${getAuthToken()}` },
            },
         );

         const resData = res?.data;
         if (resData?.statusCode !== 200) {
            setSubmitError(resData?.message || 'Failed to submit client selections');
            setSubmittingClientDiscounts(false);
            return;
         }

         const extractedPdfPath = resData?.response?.pdfPath ?? null;
         setPdfPath(extractedPdfPath);

         // Persist submission in localStorage
         try {
            const key = `clientSelectionsSubmitted:${id}`;
            localStorage.setItem(
               key,
               JSON.stringify({
                  ts: Date.now(),
                  discounts: discountPayload,
                  addons: addonPayload,
                  pdfPath: extractedPdfPath,
               }),
            );
         } catch (e) {
            /* ignore storage errors */
         }

         let successMessage = 'Your selections have been submitted successfully';
         if (hasDiscounts && hasAddons) {
            successMessage = 'Client discounts and services added successfully';
         } else if (hasDiscounts) {
            successMessage = 'Client discounts added successfully';
         } else if (hasAddons) {
            successMessage = 'Client services added successfully';
         }
         setSubmitMessage(successMessage);
         setSelectedDiscountIds([]);
         setSelectedAddonIds([]);
         setClientSubmitted(true);
      } catch (err) {
         console.error('Submit selections failed', err);
         setSubmitError(err?.response?.data?.message || err.message || 'Failed to submit');
      } finally {
         setSubmittingClientDiscounts(false);
      }
   };

   // compute totals based on fetched data
   const computeTotals = () => {
      // Apply all discounts on the original subtotal (not sequentially)
      if (!quotation)
         return {
            subtotal: 0,
            discountTotal: 0,
            addonTotal: 0,
            gstAmount: 0,
            finalTotal: 0,
            appliedAmountsById: {},
            appliedAddonAmountsById: {},
            baseAfterSelected: 0,
         };

      const subtotal = Number(
         quotation.total ??
         (quotation.quotationDetails?.reduce((s, d) => s + Number(d.total || 0), 0) || 0),
      );

      // Get selected discounts
      const selectedDiscounts = (quotationDiscounts || []).filter((d) =>
         selectedDiscountIds.includes(d.id),
      );

      let discountTotal = 0;
      const appliedAmountsById = {};

      // Calculate each discount based on the original subtotal
      selectedDiscounts.forEach((d) => {
         const type = d.type ?? d.discount?.type;
         const value = Number(d.value ?? d.discount?.value ?? 0);
         let amount = 0;
         if (type === 'percentage') {
            amount = (subtotal * value) / 100;
         } else {
            amount = value; // flat discount
         }
         // guard against negative amounts
         amount = Math.max(0, amount);
         appliedAmountsById[d.id] = amount;
         discountTotal += amount;
      });

      // guard against over-discounting
      discountTotal = Math.min(discountTotal, subtotal);

      // Get selected add-ons
      const selectedAddons = (quotationAddons || []).filter((a) => selectedAddonIds.includes(a.id));

      let addonTotal = 0;
      const appliedAddonAmountsById = {};

      // Calculate each add-on based on the original subtotal
      selectedAddons.forEach((a) => {
         const type = a.type ?? a.addonService?.type;
         const value = Number(a.value ?? a.addonService?.value ?? 0);
         let amount = 0;
         if (type === 'percentage') {
            amount = (subtotal * value) / 100;
         } else {
            amount = value; // flat add-on
         }
         // guard against negative amounts
         amount = Math.max(0, amount);
         appliedAddonAmountsById[a.id] = amount;
         addonTotal += amount;
      });

      // Calculate base after discounts and add-ons
      const baseAfterSelected = subtotal - discountTotal + addonTotal;

      const gstPercent = Number(quotation.gstPercentage ?? 0);
      const gstAmount = (baseAfterSelected * gstPercent) / 100;
      const finalTotal = baseAfterSelected + gstAmount;

      return {
         subtotal,
         discountTotal,
         addonTotal,
         gstAmount,
         finalTotal,
         appliedAmountsById,
         appliedAddonAmountsById,
         baseAfterSelected,
         gstPercent,
      };
   };

   const {
      subtotal,
      discountTotal,
      addonTotal,
      gstAmount,
      finalTotal,
      appliedAmountsById,
      appliedAddonAmountsById,
      baseAfterSelected,
      gstPercent,
   } = computeTotals();
   const currencyLabel = quotation?.currency?.symbol ?? quotation?.currency?.code ?? '';

   // show development time unit based on first quotation detail type (fallback to 'weeks')
   const timeUnitLabel =
      quotation?.quotationDetails &&
         quotation.quotationDetails.length > 0 &&
         quotation.quotationDetails[0].type
         ? quotation.quotationDetails[0].type === 'One Time'
            ? 'hours'
            : quotation.quotationDetails[0].type
         : 'weeks';

   if (loadingData) {
      return (
         <div className="quotation-wrapper">
            <div className="quotation-content">
               <p>Loading...</p>
            </div>
         </div>
      );
   }

   if (fetchError) {
      return (
         <div className="quotation-wrapper">
            <div className="quotation-content">
               <p>Error loading quotation.</p>
            </div>
         </div>
      );
   }

   return (
      <div className="quotation-wrapper">
         <style>{`
         body {
           font-family: 'Roboto', Arial, sans-serif;
           margin: 0; /* keep body neutral; layout handled by wrapper */
           background: #f4f6f9;
         }

         .quotation-wrapper {
           display: flex;
           align-items: center; /* vertical centering */
           justify-content: center; /* horizontal centering */
           min-height: 100vh;
           padding: 40px 20px;
           box-sizing: border-box;
           background: transparent;
         }

         .quotation-content {
           width: 100%;
           max-width: 1000px;
           background: #ffffff;
           padding: 30px 48px;
           box-sizing: border-box;
           margin: 20px auto;
           box-shadow: 0 10px 30px rgba(18, 38, 63, 0.08);
           border-left: 6px solid #0b76c7; /* left accent */
           border-right: 6px solid #0b76c7; /* right accent */
           border-radius: 8px;
         }

         /* Responsive helpers */
         .table-responsive {
           width: 100%;
           overflow-x: auto;
           -webkit-overflow-scrolling: touch;
         }
         .table-responsive table { min-width: 720px; }
         .table-responsive table th,
         .table-responsive table td { white-space: nowrap; }

         .header {
           display: flex;
           justify-content: space-between;
           align-items: center;
           gap: 20px;
           padding-bottom: 18px;
           border-bottom: 1px solid rgba(0,0,0,0.08);
         }

         .header-left { display:flex; align-items:center; gap:12px; }
         .logo {
           height: 60px;
           max-width: 160px;
           object-fit: contain;
           display:block;
         }

         .header-right { flex:1; display:flex; flex-direction:column; align-items:flex-end; }
         .quotation-title {
           text-align: right;
         }

         .quotation-title h1 {
           color: #0b76c7;
           font-size: 26px;
           line-height: 34px;
           font-weight: 700;
           margin: 0 0 6px;
         }

         /* two-column labels/values layout */
         .quotation-time {
           display: grid;
           grid-template-columns: auto auto;
           gap: 6px 18px;
           font-size: 13px;
           color: #6b7280;
           align-items: center;
         }
         .quotation-time .labels { text-align: left; color: #6b7280; }
         .quotation-time .values { text-align: right; color: #374151; font-weight:600; }
         .quotation-time div { margin:0; }

         .table-section { margin-top: 18px; }

         table {
           width: 100%;
           border-collapse: collapse;
           margin-top: 12px;
           font-size: 14px;
         }

         th {
           background: linear-gradient(90deg, #0b76c7 0%, #2aa0ff 100%);
           color: white;
           padding: 10px 12px;
           border: 1px solid rgba(0,0,0,0.08);
           text-align: center;
           font-weight: 700;
         }

         td {
           padding: 12px;
           border: 1px solid rgba(0,0,0,0.06);
           vertical-align: top;
           color: #1f2937;
           background: #fff;
         }

         .footer {
           display: flex;
           flex-direction: column;
           margin-top: 26px;
           gap: 18px;
         }

         .summary {
           width: 320px;
           margin-left: auto;
           border: 1px solid rgba(11,118,199,0.12);
           padding: 14px;
           background: linear-gradient(180deg, #fbfdff 0%, #f7fbff 100%);
           border-radius: 8px;
           box-shadow: 0 6px 18px rgba(11,118,199,0.03);
         }

         .summary p { margin: 6px 0; font-weight: 600; color:#0b76c7; }
         .summary .muted { font-weight:500; color:#374151; }

         .page-footer {
           margin-top: 18px;
           padding-top: 12px;
           border-top: 1px solid rgba(15,23,42,0.06);
           display:flex;
           justify-content:space-between;
           align-items:center;
           font-size:13px;
           color:#6b7280;
         }

         @media (max-width:991px) {
           .quotation-content { padding: 20px; }
           .summary { width: 100%; margin-left: 0; }
           .quotation-title { text-align: left; margin-top:12px; }
           .header { flex-direction: column; align-items: flex-start; gap: 12px; }
           .header-right { align-items: flex-start; width:100%; }
           .quotation-time { grid-template-columns: 1fr auto; width:100%; }
         }

         /* tighter mobile rules */
         @media (max-width:720px) {
           .quotation-wrapper { padding: 24px 12px; }
           .quotation-content { padding: 16px; border-left-width:4px; border-right-width:4px; }
           .logo { height: 48px; }
           th, td { padding: 8px; font-size: 13px; }
           .quotation-title h1 { font-size: 20px; }
           .table-responsive table { min-width: 640px; }
           .summary { width: 100%; margin-left: 0; }
           /* make submit button full width on small screens */
           .submit-btn { width: 100%; display: block; }
         }
      `}</style>

         <div className="quotation-content">
            {clientSubmitted ? (
               <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                  <h1
                     style={{
                        color: '#0b76c7',
                        fontSize: 32,
                        marginBottom: 12,
                     }}
                  >
                     Thank You for your response
                  </h1>
                  <p
                     style={{
                        color: '#374151',
                        fontSize: 16,
                        marginBottom: 24,
                     }}
                  >
                     We have received your request. Our team will review and get back to you soon
                  </p>

                  {pdfPath && (
                     <div
                        style={{
                           marginTop: 32,
                           padding: '20px',
                           backgroundColor: '#fbfdff',
                           borderRadius: '8px',
                           border: '1px solid rgba(11,118,199,0.12)',
                        }}
                     >
                        <p
                           style={{
                              color: '#374151',
                              fontSize: 16,
                              marginBottom: 16,
                           }}
                        >
                           A confirmation PDF with your selected discounts has been sent to your
                           email
                        </p>
                        <a
                           href={`${API_URL}${pdfPath}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           style={{
                              display: 'inline-block',
                              padding: '12px 24px',
                              backgroundColor: '#0b76c7',
                              color: '#fff',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontWeight: 600,
                              fontSize: 16,
                              transition: 'background-color 0.3s',
                              boxShadow: '0 4px 12px rgba(11, 118, 199, 0.2)',
                              cursor: 'pointer',
                           }}
                           onMouseEnter={(e) => (e.target.style.backgroundColor = '#085fa8')}
                           onMouseLeave={(e) => (e.target.style.backgroundColor = '#0b76c7')}
                        >
                           View Quotation PDF
                        </a>
                     </div>
                  )}
               </div>
            ) : clientPreviouslySubmitted ? (
               <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                  <h1
                     style={{
                        color: '#0b76c7',
                        fontSize: 32,
                        marginBottom: 12,
                     }}
                  >
                     You already submitted a Response
                  </h1>
                  <p style={{ color: '#374151', fontSize: 16 }}>
                     Thank you for your submission. Our team is reviewing your request and will get
                     back to you shortly.
                  </p>
               </div>
            ) : showOtpForm ? (
               <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <h2
                     style={{
                        color: '#0b76c7',
                        fontSize: '22px',
                        marginBottom: '16px',
                     }}
                  >
                     Access Quotation Details
                  </h2>
                  <p
                     style={{
                        color: '#374151',
                        fontSize: '15px',
                        marginBottom: '24px',
                     }}
                  >
                     To view the quotation details, please enter the OTP sent to your registered
                     email or phone number.
                  </p>

                  {infoMessage && (
                     <div
                        style={{
                           marginBottom: '16px',
                           color: '#dc2626',
                           fontSize: '14px',
                        }}
                     >
                        {infoMessage}
                     </div>
                  )}

                  <form onSubmit={handleSubmitOtp} style={{ maxWidth: '400px', margin: '0 auto' }}>
                     <input
                        type="text"
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value)}
                        placeholder="Enter OTP"
                        required
                        style={{
                           width: '100%',
                           padding: '12px',
                           borderRadius: '4px',
                           border: '1px solid rgba(0, 0, 0, 0.1)',
                           fontSize: '16px',
                           marginBottom: '16px',
                           boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                     />
                     {resError ? ((
                        <></>
                     )) : <button
                        type="submit"
                        disabled={submittingOtp}
                        style={{
                           width: '100%',
                           padding: '12px',
                           borderRadius: '4px',
                           border: 'none',
                           backgroundColor: '#0b76c7',
                           color: 'white',
                           fontSize: '16px',
                           cursor: 'pointer',
                           transition: 'background-color 0.3s',
                           boxShadow: '0 4px 12px rgba(11, 118, 199, 0.2)',
                        }}
                     >
                        {submittingOtp ? 'Verifying...' : 'Verify OTP'}
                     </button>}
                  </form>

                  {resError ? (<></>) : <div
                     style={{
                        marginTop: '16px',
                        fontSize: '14px',
                        color: '#6b7280',
                     }}
                  >
                     Didn't receive the OTP?{' '}
                     <button
                        onClick={handleRequestOtp}
                        disabled={requestingOtp}
                        style={{
                           background: 'none',
                           border: 'none',
                           color: requestingOtp ? '#9ca3af' : '#0b76c7',
                           cursor: requestingOtp ? 'not-allowed' : 'pointer',
                           padding: 0,
                           textDecoration: 'underline',
                           opacity: requestingOtp ? 0.6 : 1,
                           transition: 'all 0.3s ease',
                        }}
                     >
                        {requestingOtp ? 'Sending OTP...' : 'Resend OTP'}
                     </button>
                  </div>}
               </div>
            ) : (
               <>
                  <div className="header">
                     <div className="header-left">
                        <img src={logoUrl} alt="Service Provider's Logo" className="logo" />
                     </div>
                     <div className="header-right">
                        <div className="quotation-title">
                           <h1>Price Quotation</h1>
                        </div>
                        <div className="quotation-time" aria-hidden={false}>
                           <div className="labels">Date:</div>
                           <div className="values">
                              {quotation?.date
                                 ? new Date(quotation.date).toLocaleDateString()
                                 : '-'}
                           </div>
                           <div className="labels">Valid For:</div>
                           <div className="values">{quotation?.validTimeDay ?? '-'} Days</div>
                           <div className="labels">Quotation ID:</div>
                           <div className="values">{quotation?.uniqueId ?? '-'}</div>
                        </div>
                     </div>
                  </div>

                  <div className="table-responsive">
                     <table>
                        <tbody>
                           <tr>
                              <th colSpan="2">Service Provider</th>
                              <th colSpan="2">Client</th>
                           </tr>
                           <tr>
                              <td colSpan="2">{quotation?.serviceProvider?.name ?? '-'}</td>
                              <td colSpan="2">
                                 {quotation?.clientName ?? quotation?.user?.name ?? '-'}
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </div>

                  <div className="table-responsive">
                     <table>
                        <thead>
                           <tr>
                              <th>Description</th>
                              <th>{`Development Time (in ${timeUnitLabel})`}</th>
                              <th>{`Cost Per Hour ${quotation?.currency?.code ? ` (${quotation.currency.code})` : ''}`}</th>
                              <th>Cost - Including GST (in ₹)</th>
                           </tr>
                        </thead>
                        <tbody>
                           {(quotation?.quotationDetails ?? []).map((d) => (
                              <tr key={d.id}>
                                 <td>{d.description}</td>
                                 <td style={{ textAlign: 'center' }}>{d.developmentTime || '-'}</td>
                                 <td style={{ textAlign: 'center' }}>{d.chargePerHour || '-'}</td>
                                 <td style={{ textAlign: 'center' }}>
                                    {currencyLabel
                                       ? `${currencyLabel} ${Number(d.total).toLocaleString()}`
                                       : Number(d.total).toLocaleString()}
                                 </td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>

                  <div className="footer">
                     {quotationDiscounts.length > 0 && (
                        <div style={{ width: '100%', marginBottom: 12 }}>
                           <h5 style={{ margin: 0, color: '#0b76c7' }}>Applied Discounts</h5>
                           <div className="table-responsive">
                              <table>
                                 <thead>
                                    <tr>
                                       <th style={{ width: '60px' }}>Select</th>
                                       <th>Name</th>
                                       <th>Type</th>
                                       <th>Value</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {quotationDiscounts.map((qd) => {
                                       const type = qd.type ?? qd.discount?.type;
                                       const value = Number(qd.value ?? qd.discount?.value ?? 0);
                                       const isSelected = selectedDiscountIds.includes(qd.id);

                                       return (
                                          <tr key={qd.id}>
                                             <td style={{ textAlign: 'center' }}>
                                                <input
                                                   type="checkbox"
                                                   checked={isSelected}
                                                   onChange={() => toggleDiscount(qd.id)}
                                                />
                                             </td>
                                             <td>{qd.discount?.name ?? 'Discount'}</td>
                                             <td style={{ textAlign: 'center' }}>
                                                {type
                                                   ? type.charAt(0).toUpperCase() + type.slice(1)
                                                   : ''}
                                             </td>
                                             <td style={{ textAlign: 'center' }}>
                                                {value}
                                                {type === 'percentage' ? '%' : ''}
                                             </td>
                                          </tr>
                                       );
                                    })}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                     )}

                     {/* add-on services table */}
                     {quotationAddons.length > 0 && (
                        <div style={{ width: '100%', marginBottom: 12 }}>
                           <h5 style={{ margin: 0, color: '#0b76c7' }}>Services</h5>
                           <div className="table-responsive">
                              <table>
                                 <thead>
                                    <tr>
                                       <th style={{ width: '60px' }}>Select</th>
                                       <th>Name</th>
                                       <th>Type</th>
                                       <th>Value</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {quotationAddons.map((qa) => {
                                       const type = qa.type ?? qa.addonService?.type;
                                       const value = Number(
                                          qa.value ?? qa.addonService?.value ?? 0,
                                       );
                                       const isSelected = selectedAddonIds.includes(qa.id);

                                       return (
                                          <tr key={qa.id}>
                                             <td style={{ textAlign: 'center' }}>
                                                <input
                                                   type="checkbox"
                                                   checked={isSelected}
                                                   onChange={() => toggleAddon(qa.id)}
                                                />
                                             </td>
                                             <td>
                                                {qa.addonService?.name ?? 'service'}
                                                {qa.isRegularService ? (
                                                   <span
                                                      style={{
                                                         color: '#0b76c7',
                                                         fontStyle: 'italic',
                                                         marginLeft: 4,
                                                      }}
                                                   >
                                                      (Regular Service)
                                                   </span>
                                                ) : (
                                                   <span
                                                      style={{
                                                         color: '#0b76c7',
                                                         fontStyle: 'italic',
                                                         marginLeft: 4,
                                                      }}
                                                   >
                                                      (Add-on Service)
                                                   </span>
                                                )}
                                             </td>
                                             <td style={{ textAlign: 'center' }}>
                                                {type
                                                   ? type.charAt(0).toUpperCase() + type.slice(1)
                                                   : ''}
                                             </td>
                                             <td style={{ textAlign: 'center' }}>
                                                {value}
                                                {type === 'percentage' ? '%' : ''}
                                             </td>
                                          </tr>
                                       );
                                    })}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                     )}

                     {/* Submit section for selected discounts and services */}
                     {(quotationDiscounts.length > 0 || quotationAddons.length > 0) && (
                        <div
                           style={{
                              width: '100%',
                              marginBottom: 12,
                              display: 'flex',
                              gap: 12,
                              alignItems: 'center',
                           }}
                        >
                           <button
                              className="submit-btn"
                              onClick={handleSubmitSelections}
                              disabled={submittingClientDiscounts}
                              style={{
                                 padding: '10px 16px',
                                 background: '#0b76c7',
                                 color: '#fff',
                                 border: 'none',
                                 borderRadius: 4,
                                 cursor: 'pointer',
                              }}
                           >
                              {submittingClientDiscounts ? 'Submitting...' : 'Submit Selections'}
                           </button>

                           {submitMessage && (
                              <div style={{ color: 'green', fontWeight: 600 }}>{submitMessage}</div>
                           )}
                           {submitError && (
                              <div
                                 style={{
                                    color: '#dc2626',
                                    fontWeight: 600,
                                 }}
                              >
                                 {submitError}
                              </div>
                           )}
                        </div>
                     )}

                     <div
                        style={{
                           width: '100%',
                           display: 'flex',
                           gap: 20,
                           alignItems: 'flex-start',
                        }}
                     >
                        <div style={{ flex: 1 }}>
                           <p style={{ margin: 0, color: '#374151' }}>
                              Thank You for Your Business!
                           </p>
                        </div>

                        <div className="summary" aria-label="totals-summary">
                           <p className="muted">
                              Sub Total:{' '}
                              <span style={{ float: 'right', color: '#0b76c7' }}>
                                 {currencyLabel
                                    ? `${currencyLabel} ${subtotal.toLocaleString()}`
                                    : subtotal.toLocaleString()}
                              </span>
                           </p>
                           <p className="muted">
                              Discounts:{' '}
                              <span style={{ float: 'right' }}>
                                 -
                                 {currencyLabel
                                    ? `${currencyLabel} ${discountTotal.toLocaleString()}`
                                    : discountTotal.toLocaleString()}
                              </span>
                           </p>
                           <p className="muted">
                              Services:{' '}
                              <span style={{ float: 'right' }}>
                                 +
                                 {currencyLabel
                                    ? `${currencyLabel} ${addonTotal.toLocaleString()}`
                                    : addonTotal.toLocaleString()}
                              </span>
                           </p>
                           {gstPercent > 0 && (
                              <p className="muted">
                                 GST ({gstPercent}%):{' '}
                                 <span style={{ float: 'right', color: '#0b76c7' }}>
                                    {currencyLabel
                                       ? `${currencyLabel} ${gstAmount.toLocaleString()}`
                                       : gstAmount.toLocaleString()}
                                 </span>
                              </p>
                           )}
                           <p
                              style={{
                                 marginTop: 8,
                                 fontSize: 16,
                                 fontWeight: 800,
                                 color: '#0b76c7',
                              }}
                           >
                              Total:{' '}
                              <span style={{ float: 'right' }}>
                                 {currencyLabel
                                    ? `${currencyLabel} ${finalTotal.toLocaleString()}`
                                    : finalTotal.toLocaleString()}
                              </span>
                           </p>
                        </div>
                     </div>

                     <div className="page-footer">
                        <div>
                           © {new Date().getFullYear()} {serviceProviderName ?? 'Pitangent'}. All
                           rights reserved.
                        </div>
                        <div>Version 1.0.0</div>
                     </div>
                  </div>
               </>
            )}
         </div>
      </div>
   );
};

export default QuotationClient;
